'use client';

import { useMemo, useState, useRef, useEffect, Suspense, useCallback } from 'react';
import { ArrowDown, Maximize2 } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { useRouter } from 'next/navigation';
import {
    emitHomeSearch,
    HOME_SEARCH_EVENT,
    HomeSearchDetail,
} from '@/features/listings/components/HomeSearchMode';
import TextareaAutosize from 'react-textarea-autosize';
import { useVoxCtx } from '@/features/vox/lib/VoxProvider';
import { Img } from '@/shared/components/ui/Img';
import { Pill } from '@/shared/components/ui/Pill';
import { MOTION, LIFT } from '@/shared/components/ui/motion';
import { useSearchParams } from 'next/navigation';
import { AdvertsClassicFilter } from './AdvertsClassicFilter';
import { ListingsFilterSettings } from '../../listings/types';

/** DOM event pre VOX chat dock (features/vox) - string namiesto importu, aby filter a vox neboli import-coupled */
const VOX_CHAT_OPEN_EVENT = 'rx-vox-chat-open';

// type TopFilterActiveKey = ListingTxnType | 'DEMAND' | 'MORE' | '';
type TopFilterActiveKey = 'LISTING' | 'DEMAND' | 'MORE' | '';

type TopFilterLink = {
    key: TopFilterActiveKey;
    text: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    position: 'left' | 'right';
    imgSrc?: string;
    href?: string;
};

interface Props {
    showNav: boolean;
    showUseClassicFilter: boolean;
    onSetClassicFilter: () => void;
    /**
     * Homepage hero rezim: glass morph karta, plazmovy input (vox-gb ring
     * + focus glow), VOX mic vedla inputu a vlozene klasicke filtre.
     * Bez hero = povodna samostatna smart karta (napr. /inzeraty).
     */
    hero?: boolean;
    /** Hero: klasicke filtre vlozene priamo v glass karte. */
    filterSettings?: ListingsFilterSettings;
    /** Hero: VOX mic button slot z page. */
    voiceButton?: React.ReactNode;
}

/* Rotujuce hinty v hero inpute - par punchy lines, ako to pouzivat.
   Meni sa kazde ~4s s fade transition. */
/* Hover na mic - mikrofonova napoveda v inpute. */
const MIC_HOVER_HINT =
    'Ak ťa prehliadač požiada o\u00A0mikrofón, kľudne to odklikni - inak sa nebudeme počuť\u00A0:)';

const HERO_HINTS = [
    'Skús: „Hľadáme dom so záhradou pri Bratislave pre rodinku so psíkom.“',
    '„2-izbák v\u00A0Ružinove na invest do 200 000 €. Čo máš?“',
    '„Novostavba s\u00A0balkónom a\u00A0výhľadom. Prekvap ma.“',
    'Napíš alebo povedz - Realica ti rozumie.',
];

/* AI spark - rovnaky glyph ako vo VOX (VoxStage), zamerne lokalna kopia. */
function SparkGlyph({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn('rx-spark', className)} aria-hidden>
            <path
                className="rx-spark-big"
                d="M12 2.4l1.95 5.85 5.85 1.95-5.85 1.95L12 18l-1.95-5.85L4.2 10.2l5.85-1.95L12 2.4Z"
                fill="currentColor"
            />
            <path
                className="rx-spark-small"
                d="M19.1 14.6l.85 2.55 2.55.85-2.55.85-.85 2.55-.85-2.55-2.55-.85 2.55-.85.85-2.55Z"
                fill="currentColor"
            />
        </svg>
    );
}

/* Glass morph povrch hero karty - polopriehladna biela na zltom hero,
   blur zoberie gauc v pozadi. */
const CARD_GLASS = cn(
    'bg-white/25 backdrop-blur-[16px] border border-white/40',
    'shadow-[0_11px_30px_0_rgba(0,0,0,0.10)]',
);

export function AdvertsSmartFilter(props: Props) {
    const router = useRouter();
    const hero = props.hero === true;

    // state
    const [topFilterActiveKey, setTopFilterActiveKey] = useState<TopFilterActiveKey>('LISTING');

    const TOP_LINKS: Array<TopFilterLink> = useMemo(
        () => [
            {
                key: 'LISTING',
                text: 'Ponuka',
                onClick: (e) => {
                    e.preventDefault();
                    handleTopSearchClick('LISTING');
                    // handleTopSearchClick('SALE');
                },
                position: 'left',
            },
            {
                key: 'DEMAND',
                text: 'Dopyt',
                onClick: (e) => {
                    e.preventDefault();
                    handleTopSearchClick('DEMAND');
                    // handleTopSearchClick('RENT');
                },
                position: 'left',
            },
            {
                key: 'MORE',
                text: 'Objav viac',
                position: 'left',
                onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    setTopFilterActiveKey('MORE');
                    // handleTopSearchClick('MORE');
                },
            },
            {
                key: '',
                text: 'Realitné kancelárie',
                href: '/',
                position: 'right',
                imgSrc: '/images/ico_developeri.svg',
            },
            {
                key: '',
                text: 'Realitní markéri',
                href: '/makleri',
                position: 'right',
                imgSrc: '/images/ico_makleri.svg',
            },
            {
                key: '',
                text: 'Developeri',
                href: '/',
                position: 'right',
                imgSrc: '/images/ico_rk.svg',
            },
        ],
        [],
    );

    const topLinks = useMemo(() => {
        return TOP_LINKS.map((i) => ({
            jsx: (
                <Pill
                    href={i.href ?? '#'}
                    state={topFilterActiveKey === i.key ? 'active' : 'inactive'}
                    onClick={(e) => {
                        if (i.href) {
                            router.push(i.href);
                            return;
                        } else {
                            e.preventDefault();
                            i.onClick?.(e);
                        }
                    }}
                >
                    {i.text}
                </Pill>
            ),
            position: i.position,
        }));
    }, [TOP_LINKS, topFilterActiveKey]);

    /* Smart filter */
    const smartTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    /**
     * Rozhovor bezi PRIAMO V TEJTO LISTE, nie v samostatnom doku.
     * Karta sa smerom dole roztiahne a zobrazi poslednych par replik.
     */
    const vox = useVoxCtx();
    const voxAktivny = !!vox && (vox.phase !== 'idle' || vox.messages.length > 0);
    const rozhovorRef = useRef<HTMLDivElement | null>(null);

    const [smartVoice, setSmartVoice] = useState(false);
    const [smartIsMultiline, setSmartIsMultiline] = useState(false);
    const [smartInputBaseHeight, setSmartInputBaseHeight] = useState<number | null>(null);
    const [smartValue, setSmartValue] = useState('');
    const [smartInputPlaceholder, setSmartInputPlaceholder] = useState(true);

    useEffect(() => {
        const el = smartTextareaRef.current;
        if (!el) return;

        setSmartInputBaseHeight(el.clientHeight);
    }, []);

    /* Klasicke vyhladavanie v hero: toggle roztvara 3 povodne fieldy pod
       inputom. Karta sa plynulo natiahne, radius sa premeni pill -> 18/25.
       classicSettled prepne overflow na visible az PO dojazde animacie,
       aby sa dropdowny polia neorezavali. */
    const [classicOpen, setClassicOpen] = useState(false);
    const [classicSettled, setClassicSettled] = useState(false);
    /** hover na toggle - v inpute sa ukaze "Alebo použi klasiku" */
    const [classicHover, setClassicHover] = useState(false);
    /** hover na mic - v inpute sa ukaze mikrofonova napoveda */
    const [micHover, setMicHover] = useState(false);

    useEffect(() => {
        if (!classicOpen) {
            setClassicSettled(false);
            return;
        }
        const t = setTimeout(() => setClassicSettled(true), 440);
        return () => clearTimeout(t);
    }, [classicOpen]);

    /* Rotacia hero hintov: fade out -> swap -> fade in */
    const [hintIdx, setHintIdx] = useState(0);
    const [hintVisible, setHintVisible] = useState(true);

    useEffect(() => {
        if (!hero) return;
        let swap: ReturnType<typeof setTimeout>;
        const iv = setInterval(() => {
            setHintVisible(false);
            swap = setTimeout(() => {
                setHintIdx((i) => (i + 1) % HERO_HINTS.length);
                setHintVisible(true);
            }, 420);
        }, 4200);
        return () => {
            clearInterval(iv);
            clearTimeout(swap);
        };
    }, [hero]);

    // reset z loga: vycisti smart input (textarea je uncontrolled)
    useEffect(() => {
        if (!hero) return;
        const onReset = (e: Event) => {
            const d = (e as CustomEvent<HomeSearchDetail>).detail;
            if (d?.source !== 'reset') return;
            if (smartTextareaRef.current) smartTextareaRef.current.value = '';
            setSmartValue('');
            setSmartInputPlaceholder(true);
            setSmartIsMultiline(false);
        };
        window.addEventListener(HOME_SEARCH_EVENT, onReset);
        return () => window.removeEventListener(HOME_SEARCH_EVENT, onReset);
    }, [hero]);

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const el = e.target;
        const nextValue = e.target.value;

        setSmartValue(nextValue);
        if (smartInputPlaceholder) setSmartInputPlaceholder(false);

        // homepage search mode: prvy znak zapne split view, prazdny input vrati klasicku homepage
        if (hero) emitHomeSearch(nextValue.trim().length > 0, 'input', nextValue.trim());

        // multiline je zatial vypnuty (maxRows 1) - detekcia by ho falosne zapla, ked wrap=off
        // vytvori horizontalny scroll a clientHeight naskoci. Vratit, ked chat v inpute povolime.
        void el;
        void smartInputBaseHeight;
        if (smartIsMultiline) setSmartIsMultiline(false);
    }

    /* Odoslanie hero inputu - otvori VOX chat s textom (rovnaky vzor ako island). */
    function smartSubmit() {
        const text = smartValue.trim();
        if (!text) return;
        // Ked bezi engine, text ide do prebiehajuceho rozhovoru v tejto liste.
        // Bez enginu ostava povodne spravanie - otvori sa dok.
        if (vox) vox.sendText(text);
        else window.dispatchEvent(new CustomEvent(VOX_CHAT_OPEN_EVENT, { detail: { text } }));
        setSmartValue('');
        setSmartInputPlaceholder(true);
        setSmartIsMultiline(false);
    }

    function handleTopSearchClick(key: TopFilterActiveKey) {
        setTopFilterActiveKey(key);
    }

    /* Rad piluliek (5 buttons) - v hero sedia nad glass kartou. */
    const pillsRow = (
        <div className="lg:flex lg:items-center lg:justify-between">
            <div className="flex gap-[7px] xl:gap-[10px]">
                {topLinks ? (
                    topLinks
                        .filter((i) => i.position === 'left')
                        .map((link, i) => (
                            <div key={i} className="flex flex-1 lg:grow-0 lg:last:hidden">
                                {link.jsx}
                            </div>
                        ))
                ) : (
                    <></>
                )}
            </div>
            <div className="gap-[7px] hidden lg:flex xl:gap-[10px]">
                {props.showNav && topLinks ? (
                    topLinks
                        .filter((i) => i.position === 'right')
                        .map((link, i) => (
                            <div key={i} className="flex flex-1 lg:grow-0">
                                {link.jsx}
                            </div>
                        ))
                ) : (
                    <></>
                )}
            </div>
        </div>
    );

    /* Panel "Objav viac" - obsah karty po kliku na tretiu pilulku. */
    const morePanel = (
        <div className="py-[20px]">
            <div className="flex">
                {TOP_LINKS.filter((i) => i.position === 'right').map((link, i) => {
                    return (
                        <div
                            key={i}
                            className="flex-1 text-center flex flex-col items-center gap-[15px] border-l first:border-l-0 px-[20px] py-[10px]"
                        >
                            <Img src={link.imgSrc as string} alt={link.text} className="w-[30px]" />
                            <p className="text-[13px] font-[500]">{link.text}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    /* Mic button - staticka verzia pre samostatnu smart kartu.
       V hero ho nahradza VOX plazmovy mic zo slotu (props.voiceButton). */
    const micButton = (outside: boolean) => (
        <button
            className={cn('cursor-pointer hover:opacity-85 shrink-0', LIFT, MOTION)}
            onClick={() => {}}
        >
            <Img
                className={cn(
                    outside
                        ? 'size-[40px] md:size-[45px] xl:size-[52px]'
                        : 'size-[32px] md:size-[35px] xl:size-[42px]',
                )}
                src="/images/home/button_mic.svg"
                alt="AI voice"
            />
        </button>
    );

    /* Input blok - textarea + placeholder + sipka (+ volitelne mic vnutri).
       plasma = VOX zlaty rotujuci border + blurry glow na focus. */
    const makeInputBlock = (opts: {
        micInside: boolean;
        blackArrow: boolean;
        plasma?: boolean;
        /** VOX mic vlozeny priamo v inpute (hero) */
        micNode?: React.ReactNode;
    }) => (
        <div
            className={cn(
                'relative w-full',
                // Pocas rozhovoru je to JEDNA cista biela karta: input hore, rozhovor pod nim.
                // Triedy vox-fieldw a rx-hero-plasma sa vtedy NEPOUZIVAJU - ich pravidlo
                // :focus-within prida inputu zlaty tien, ktory presvital v rohoch
                // ako dva zlte obluky.
                opts.plasma &&
                    (voxAktivny && !!vox
                        ? 'rounded-[28px] bg-white shadow-[0_11px_30px_0_rgba(0,0,0,0.15)]'
                        : 'vox-fieldw rx-hero-plasma rounded-full'),
            )}
        >
            <div className="relative w-full">
            {/* Plazmovy prstenec a hviezdicka patria k INPUTU, nie k celej karte.
                Pocas rozhovoru sa schovaju uplne: prstenec je kruhovy a jeho rohy
                prekryvali text vlavo aj mikrofon vpravo. Karta je vtedy cista biela. */}
            {opts.plasma && !(voxAktivny && !!vox) && (
                <>
                    <span aria-hidden className="vox-fglow" />
                    <span aria-hidden className="vox-gb" />
                    <span
                        aria-hidden
                        className="pointer-events-none absolute z-[4] left-[15px] md:left-[18px] xl:left-[20px] top-1/2 -translate-y-1/2 text-[#f3c300]"
                    >
                        <SparkGlyph className="size-[18px] md:size-[20px] xl:size-[23px]" />
                    </span>
                </>
            )}
            <TextareaAutosize
                ref={smartTextareaRef}
                value={smartValue}
                // rows=1 aj v SSR HTML: browser default je rows=2 (82px), autosize to po hydratacii
                // stiahol na 60px -> 22px skok celeho hero. S rows=1 sedi SSR vyska s finalnou.
                rows={1}
                minRows={1}
                // zatial jednoriadkovy: viac riadkov nedovolime, chat sa otvara ikonou
                maxRows={1}
                wrap="off"
                className={cn(
                    // wrap=off: dlhy text sa nezalamuje, roluje horizontalne za caretom (scrollbar skryty)
                    'overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    'block relative z-[3] w-full min-h-[45px] resize-none rounded-[25px] border px-[15px] py-[13px] leading-[19px] text-[13px] shadow-[0_11px_30px_0_rgba(0,0,0,0.15)]',
                    'focus-visible:outline-none focus-visible:ring-1',
                    'md:text-[13px] md:px-[20px] md:min-h-[50px] md:py-[15px] md:leading-[20px]',
                    'lg:min-h-[50px] lg:text-[14px] lg:py-[14.5px] lg:leading-[21px]',
                    'xl:min-h-[60px] xl:text-[15px] xl:py-[19px] xl:leading-[22px]',
                    hero && 'bg-white',
                    // V rezime rozhovoru: rovne spodne rohy (nadvazuje na panel),
                    // ziadny tien a text zacina zlava - hviezdicka je schovana.
                    opts.plasma &&
                        voxAktivny &&
                        !!vox &&
                        cn(
                            'shadow-none bg-transparent',
                            // ziadne zaoblenie - input je hornou castou karty
                            'rounded-none xl:rounded-none',
                            'pl-[22px] md:pl-[28px] xl:pl-[32px]',
                        ),
                    opts.plasma &&
                        'border-0 focus-visible:ring-0 rounded-full xl:rounded-full pl-[42px] md:pl-[48px] xl:pl-[54px]',
                    smartIsMultiline
                        ? 'pb-[40px] md:pb-[50px] rounded-[20px] md:rounded-[25px] lg:pb-[60px] xl:pb-[70px] xl:rounded-[25px]'
                        : opts.micNode
                          ? 'pr-[130px] md:pr-[145px] lg:pr-[155px] xl:pr-[170px] xl:rounded-[40px]'
                          : opts.micInside
                            ? 'pr-[90px] md:pr-[100px] lg:pr-[110px] xl:pr-[120px] xl:rounded-[40px]'
                            : 'pr-[50px] md:pr-[55px] lg:pr-[60px] xl:pr-[70px] xl:rounded-[40px]',
                )}
                onChange={handleChange}
                onKeyDown={(e) => {
                    // Enter nikdy nevlozi novy riadok (Shift+Enter tiez nie)
                    if (e.key === 'Enter') e.preventDefault();
                }}
                onPaste={(e) => {
                    // vlozeny text s novymi riadkami zlucit do jedneho riadku
                    const text = e.clipboardData.getData('text');
                    if (!/[\r\n]/.test(text)) return;
                    e.preventDefault();
                    const el = e.currentTarget;
                    const clean = text.replace(/\s*[\r\n]+\s*/g, ' ');
                    const start = el.selectionStart ?? el.value.length;
                    const end = el.selectionEnd ?? el.value.length;
                    el.value = el.value.slice(0, start) + clean + el.value.slice(end);
                    el.selectionStart = el.selectionEnd = start + clean.length;
                    handleChange({ target: el } as React.ChangeEvent<HTMLTextAreaElement>);
                }}
            />

            {/* Rotujuci hint pocas rozhovoru netreba - clovek uz vie, co robit. */}
            {!smartValue && smartInputPlaceholder && !(voxAktivny && !!vox) && (
                <div
                    className={cn(
                        // z-[4]: nad textareou (z-3) - v hero ma biele pozadie,
                        // ktore by inak placeholder prekrylo
                        'rx-hero-hint w-full pointer-events-none absolute inset-0 z-[4]',
                        opts.plasma
                            ? 'pl-[46px] md:pl-[52px] xl:pl-[58px] pr-[135px] md:pr-[150px] xl:pr-[175px]'
                            : cn('px-[25px]', opts.micInside ? 'pr-[95px]' : 'pr-[60px]'),
                        'flex justify-center items-center text-center',
                        'md:min-h-[50px]',
                        'lg:min-h-[50px]',
                        'xl:min-h-[60px]',
                    )}
                >
                    {opts.plasma ? (
                        <span className="relative block w-full">
                            <p
                                className={cn(
                                    'truncate text-[13px] font-[300] tracking-[0.015em] md:text-[14px] lg:text-[16px] xl:text-[17px]',
                                    'transition duration-[420ms] ease-[var(--ease-rx)]',
                                    !classicHover && !micHover && hintVisible
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-[5px]',
                                )}
                            >
                                {HERO_HINTS[hintIdx]}
                            </p>
                            {/* hover na klasika toggle - kus bolder vyzva */}
                            <p
                                className={cn(
                                    'absolute inset-0 truncate text-[13px] font-[700] text-black md:text-[14px] lg:text-[16px] xl:text-[17px]',
                                    'transition duration-[220ms] ease-[var(--ease-rx)]',
                                    classicHover && !micHover ? 'opacity-100' : 'opacity-0',
                                )}
                            >
                                Alebo použi klasiku
                            </p>
                            {/* hover na mic - mikrofonova napoveda */}
                            <p
                                className={cn(
                                    'absolute inset-0 truncate self-center text-[11px] font-[400] text-black/55 md:text-[12px] xl:text-[13px]',
                                    'transition duration-[220ms] ease-[var(--ease-rx)]',
                                    micHover ? 'opacity-100' : 'opacity-0',
                                )}
                            >
                                {MIC_HOVER_HINT}
                            </p>
                        </span>
                    ) : (
                        <p
                            className={cn(
                                'truncate text-[13px] font-[300] md:text-[14px] lg:text-[16px] xl:text-[17px]',
                            )}
                        >
                            Skús: „Hľadáme dom so záhradou pri Bratislave, kam sa zmestí rodinka so
                            prískom.“
                        </p>
                    )}
                </div>
            )}

            <div
                className={cn(
                    // jednoriadkovy input -> ovladace vertikalne na stred (items-end bolo pre multiline)
                    'rx-hero-inputctl absolute z-[4] h-full right-[10px] bottom-0 flex items-center gap-[5px]',
                    'md:right-[12px] md:gap-[8px]',
                    'lg:right-[10px] lg:gap-[7px]',
                    'xl:gap-[8px] xl:right-[13px]',
                )}
            >
                {opts.micNode && (
                    <button
                        aria-label="Otvoriť chat"
                        title="Otvoriť chat"
                        className={cn(
                            'size-[30px] flex items-center justify-center rounded-full cursor-pointer',
                            'bg-transparent text-black hover:bg-[#f8e5b0]',
                            LIFT,
                            MOTION,
                        )}
                        onClick={() => {
                            // S beziacim enginom je rozhovor priamo tu - tlacidlo len
                            // spusti mikrofon. Dok ostava pre pripad bez enginu.
                            if (vox) {
                                if (vox.phase === 'idle') vox.start();
                                return;
                            }
                            window.dispatchEvent(new CustomEvent('rx-vox-chat-open'));
                        }}
                    >
                        {/* strokeWidth 2.4 pri 15px = 1.5px absolutne, rovnako ako ArrowDown 18px @ 2 */}
                        <Maximize2 className="size-[15px]" strokeWidth={2.4} />
                    </button>
                )}
                <button
                    className={cn(
                        'size-[30px] flex items-center justify-center rounded-full cursor-pointer',
                        // opticke dorovnanie: vizualny gap expand->sipka = sipka->VOX button
                        '-ml-[4px] md:-ml-[4px] xl:-ml-[4px]',
                        opts.blackArrow
                            ? 'bg-black hover:bg-[#222]'
                            : 'bg-transparent hover:bg-[#f8e5b0]',
                        LIFT,
                        MOTION,
                    )}
                    onClick={smartSubmit}
                >
                    {/* rovnaka sipka ako toggle klasiky (lucide ArrowDown), otocena doprava */}
                    <ArrowDown
                        className={cn('size-[16px] xl:size-[18px] -rotate-90', opts.blackArrow && 'text-white')}
                    />
                </button>

                {opts.micInside && micButton(false)}
                {opts.micNode && (
                    <span
                        className="flex"
                        onPointerEnter={(e) => {
                            if (e.pointerType === 'mouse') setMicHover(true);
                        }}
                        onPointerLeave={() => setMicHover(false)}
                    >
                        {opts.micNode}
                    </span>
                )}
            </div>
            </div>

            {/* Rozhovor priamo v liste. Rozbaluje sa smerom dole - ziadny samostatny dok. */}
            {opts.plasma && (
                <div
                    className={cn(
                        'relative z-[5] overflow-hidden rounded-b-[28px]',
                        'transition-[max-height,opacity] duration-[420ms] ease-[var(--ease-rx)]',
                        // PEVNA vyska, nie rast podla obsahu. Kazda nova replika by
                        // inak kartu skokovo predlzila a "Rozmyslam" by ju pri kazdej
                        // odpovedi natiahol a zase stiahol.
                        voxAktivny && !!vox ? 'max-h-[176px] opacity-100' : 'max-h-0 opacity-0',
                    )}
                >
                    <div
                        ref={rozhovorRef}
                        onScroll={naPosun}
                        className={cn(
                            // Ziadne vlastne pozadie ani tien - panel je sucastou karty,
                            // oddeluje ho len tenka linka pod inputom.
                            // relative z-[5]: nad plazmovym prstencom, ktory by inak
                            // presvital do textu. Vlastne biele pozadie, aby bol text citatelny.
                            'rx-vox-talk relative z-[5] h-[176px] overflow-y-auto bg-white',
                            'rounded-b-[28px]',
                            // Zhodne odsadenie s inputom, aby text zacinal v jednej linii
                            'pl-[22px] pr-[22px] pt-[10px] pb-[16px] md:pl-[28px] md:pr-[28px] xl:pl-[32px] xl:pr-[32px]',
                        )}
                    >
                        {vox && vox.messages.length === 0 && (
                            <p className="text-[13px] leading-[20px] text-black/45">
                                {vox.phase === 'connecting' ? 'Spájam sa...' : 'Počúvam - povedz, čo hľadáš.'}
                            </p>
                        )}

                        {vox &&
                            vox.messages.slice(-6).map((m) => (
                                <p
                                    key={m.id}
                                    className={cn(
                                        'text-[13px] leading-[1.5] mb-[7px] last:mb-0 md:text-[14px]',
                                        m.role === 'user' ? 'text-black font-[500]' : 'text-black/70',
                                    )}
                                >
                                    <span className="text-black/35 mr-[6px]">
                                        {m.role === 'user' ? 'Ty' : 'Realica'}
                                    </span>
                                    {m.text}
                                </p>
                            ))}

                        {/* Riadok stavu je pritomny VZDY - meni sa len jeho viditelnost.
                            Keby sa pridaval a odoberal, karta by pri kazdej odpovedi
                            poskocila o jeho vysku. */}
                        <p
                            aria-live="polite"
                            className={cn(
                                'text-[13px] text-black/40 h-[20px] leading-[20px]',
                                'transition-opacity duration-[200ms] ease-[var(--ease-rx)]',
                                vox && (vox.phase === 'thinking' || vox.phase === 'speaking')
                                    ? 'opacity-100'
                                    : 'opacity-0',
                            )}
                        >
                            {vox && vox.phase === 'speaking' ? 'Realica hovorí...' : 'Rozmýšľam...'}
                        </p>
                    </div>

                </div>
            )}
        </div>
    );

    /**
     * Drzime sa spodku, kym si clovek sam neposunie nahor.
     *
     * Predtym sa panel po KAZDEJ replike stiahol na spodok natvrdo, takze
     * posuvanie v historii bolo nemozne - hned to skocilo spat.
     */
    const drzSaSpodku = useRef(true);

    const naPosun = () => {
        const el = rozhovorRef.current;
        if (!el) return;
        // Tolerancia 28 px - pri plynulom posuve sa spodok netrafi presne
        drzSaSpodku.current = el.scrollHeight - el.scrollTop - el.clientHeight < 28;
    };

    useEffect(() => {
        const el = rozhovorRef.current;
        if (!el || !voxAktivny || !drzSaSpodku.current) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [vox?.messages.length, vox?.phase, voxAktivny]);

    // Novy rozhovor zacina zase pri spodku
    useEffect(() => {
        if (!voxAktivny) drzSaSpodku.current = true;
    }, [voxAktivny]);

    /* Mikrofonova napoveda pod inputom - len samostatna smart karta. */
    const micHint = (
        <small className="text-[10px] leading-[120%] text-[#777777] md:text-[11px] xl:text-[12px]">
            Ak ťa prehliadač požiada o prístup k mikrofónu nezľakni sa a kľudne to odklikni :)
        </small>
    );

    const classicLink = props.showUseClassicFilter && (
        <div>
            <a
                href="#"
                className="text-[10px] leading-[120%] text-[#aaa] md:text-[11px] xl:text-[12px]"
                onClick={() => props.onSetClassicFilter()}
            >
                Použiť klasický filter
            </a>
        </div>
    );

    /* ===== HERO (homepage): pills nad kartou, glass card = plazmovy input
       + VOX mic mimo inputu + vlozene klasicke filtre ===== */
    if (hero) {
        return (
            <>
                <div className="rx-hero-pills">{pillsRow}</div>
                <div
                    className={cn(
                        'rx-hero-card mt-[12px] xl:mt-[16px]',
                        classicOpen && 'rx-hero-card--classic',
                        classicOpen
                            ? 'rounded-[18px] xl:rounded-[25px]'
                            : 'rounded-[45px] xl:rounded-[55px]',
                        'transition-[border-radius] duration-[420ms] ease-[var(--ease-rx)]',
                        CARD_GLASS,
                        'p-[11px] md:p-[20px] xl:p-[25px]',
                    )}
                >
                    {topFilterActiveKey === 'MORE' ? (
                        morePanel
                    ) : (
                        <>
                            <div className="rx-hero-inputrow flex items-center gap-[10px] md:gap-[12px] xl:gap-[15px]">
                                <div className="flex-1 min-w-0">
                                    {makeInputBlock({
                                        micInside: false,
                                        blackArrow: false,
                                        plasma: true,
                                        micNode: props.voiceButton ?? micButton(false),
                                    })}
                                </div>
                                {/* Toggle klasickeho vyhladavania - plus rotuje na X */}
                                <a
                                    href="#"
                                    aria-label="Klasické vyhľadávanie"
                                    title="Klasické vyhľadávanie"
                                    aria-expanded={classicOpen}
                                    className={cn(
                                        'flex justify-center items-center rounded-full cursor-pointer shrink-0',
                                        'size-[30px] min-w-[30px]',
                                        'duration-[260ms]',
                                        classicOpen
                                            ? 'bg-[#f8e5b0] text-black'
                                            : cn(
                                                  'bg-transparent text-black/80',
                                                  'hover:bg-[#f8e5b0] hover:text-black',
                                              ),
                                        LIFT,
                                        MOTION,
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setClassicOpen((o) => !o);
                                    }}
                                    onPointerEnter={(e) => {
                                        if (e.pointerType === 'mouse') setClassicHover(true);
                                    }}
                                    onPointerLeave={() => setClassicHover(false)}
                                >
                                    <ArrowDown
                                        className={cn(
                                            'size-[16px] xl:size-[18px]',
                                            'transition duration-[300ms] ease-[var(--ease-rx)]',
                                            classicOpen ? 'rotate-180' : 'rotate-0',
                                        )}
                                    />
                                </a>
                            </div>

                            {/* Klasicke fieldy - vysuvaju sa pod input riadkom */}
                            <div
                                className={cn(
                                    'rx-hero-classic grid transition-[grid-template-rows] duration-[420ms] ease-[var(--ease-rx)]',
                                    // zatvaranie: najprv fieldy zmiznu (opacity), potom sa zlozi vyska
                                    classicOpen ? 'grid-rows-[1fr] delay-0' : 'grid-rows-[0fr] delay-[120ms]',
                                )}
                            >
                                <div
                                    className={cn(
                                        'min-h-0',
                                        classicSettled ? 'overflow-visible' : 'overflow-hidden',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            'pb-0',
                                            // otvaranie: vyska sa rozbali a fieldy sa objavia cez opacity (nie maska) - fade
                                            // zacina az ked je miesto pripravene, s jemnym posunom hore
                                            'transition-[opacity,translate] ease-[var(--ease-rx)]',
                                            classicOpen
                                                ? 'opacity-100 translate-y-0 duration-[360ms] delay-[220ms]'
                                                : 'opacity-0 translate-y-[6px] duration-[160ms] delay-0',
                                        )}
                                    >
                                        {props.filterSettings && (
                                            <AdvertsClassicFilter
                                                embedded
                                                filterSettings={props.filterSettings}
                                                showUseSmartFilter={false}
                                                onSetSmartFilter={() => {}}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

            </>
        );
    }

    /* ===== Samostatna smart karta (napr. /inzeraty) ===== */
    return (
        <>
            {pillsRow}

            {/* Smart filter */}
            <div
                className={cn(
                    'mt-[10px] bg-[#fff] rounded-[18px] shadow-[0_11px_30px_0_rgba(0,0,0,0.15)] xl:mt-[13px]',
                    'xl:rounded-[25px]',
                )}
            >
                {topFilterActiveKey === 'MORE' ? (
                    morePanel
                ) : (
                    <div
                        className={cn('p-[20px] md:p-[35px] md:px-[50px] xl:p-[40px] xl:px-[70px]')}
                    >
                        <div className="flex flex-col gap-[15px] text-center md:gap-[20px] xl:gap-[25px]">
                            <p className="font-[700] text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px]">
                                Povedz, napíš alebo sa opýtaj - nájdime to spolu!
                            </p>
                            {makeInputBlock({ micInside: true, blackArrow: false })}
                            {micHint}
                            {classicLink}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

function GetSearchParams({
    onSearchParams,
}: {
    onSearchParams: (params: URLSearchParams) => void;
}) {
    const searchParams = useSearchParams();
    useEffect(() => {
        if (searchParams) {
            onSearchParams(searchParams);
        }
    }, [searchParams]);
    return <></>;
}
